import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderKanban, ArrowLeft, Settings, Users, Calendar, Video,
  FileText, ListChecks, Target, Clock, CheckCircle, AlertCircle,
  Plus, Play, Mic, MessageCircle, Mail, MoreVertical, Edit,
  Trash2, ExternalLink, TrendingUp, Activity, ChevronRight
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-800',
};

const taskStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const priorityColors = {
  low: 'text-slate-500',
  medium: 'text-blue-500',
  high: 'text-orange-500',
  critical: 'text-red-500',
};

const eventTypeIcons = {
  project_created: FolderKanban,
  member_added: Users,
  meeting_scheduled: Calendar,
  meeting_started: Play,
  meeting_completed: CheckCircle,
  recording_uploaded: Video,
  transcript_ready: FileText,
  analysis_complete: TrendingUp,
  mom_generated: FileText,
  mom_approved: CheckCircle,
  task_created: ListChecks,
  task_completed: CheckCircle,
  decision_made: Target,
  whatsapp_notification: MessageCircle,
};

export default function ProjectDashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('auth_token');
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState({});
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [recentDecisions, setRecentDecisions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/meeting-intelligence/projects/${projectId}/dashboard?token=${token}`
      );
      
      if (response.data.success) {
        setProject(response.data.project);
        setStats(response.data.stats || {});
        setRecentMeetings(response.data.recent_meetings || []);
        setPendingTasks(response.data.pending_tasks || []);
        setRecentDecisions(response.data.recent_decisions || []);
        setTimeline(response.data.timeline || []);
        setMembers(response.data.members || []);
      }
    } catch (error) {
      toast.error('Failed to load project dashboard');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-12 bg-slate-200 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 text-center">
        <FolderKanban className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">Project not found</h2>
        <Button onClick={() => navigate('/meeting-intelligence/projects')}>
          Back to Projects
        </Button>
      </div>
    );
  }

  const taskProgress = stats.total_tasks > 0 
    ? Math.round((stats.completed_tasks / stats.total_tasks) * 100) 
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="project-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/meeting-intelligence/projects')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{project.name}</h1>
              <Badge className={statusColors[project.status]}>{project.status}</Badge>
            </div>
            <p className="text-muted-foreground text-sm flex items-center gap-2">
              <span>{project.code}</span>
              {project.description && <span>• {project.description}</span>}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/meeting-intelligence/meetings/new?project=${projectId}`)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Meeting
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Edit className="h-4 w-4 mr-2" />
                Edit Project
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Users className="h-4 w-4 mr-2" />
                Manage Members
              </DropdownMenuItem>
              <DropdownMenuItem>
                <MessageCircle className="h-4 w-4 mr-2" />
                Link WhatsApp
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Archive Project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meetings</p>
                <p className="text-2xl font-bold">{stats.total_meetings || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Video className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks</p>
                <p className="text-2xl font-bold">
                  {stats.completed_tasks || 0}/{stats.total_tasks || 0}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                <ListChecks className="h-5 w-5 text-green-600" />
              </div>
            </div>
            {stats.total_tasks > 0 && (
              <Progress value={taskProgress} className="mt-2 h-1" />
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Decisions</p>
                <p className="text-2xl font-bold">{stats.total_decisions || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Team</p>
                <p className="text-2xl font-bold">{stats.total_participants || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Meetings */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Video className="h-5 w-5 text-blue-500" />
                  Recent Meetings
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/meeting-intelligence/projects/${projectId}/meetings`}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {recentMeetings.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No meetings yet</p>
                    <Button 
                      variant="link" 
                      onClick={() => navigate(`/meeting-intelligence/meetings/new?project=${projectId}`)}
                    >
                      Create your first meeting
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentMeetings.map(meeting => (
                      <div 
                        key={meeting.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            meeting.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'
                          }`}>
                            {meeting.status === 'completed' ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Clock className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{meeting.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(meeting.scheduled_start)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {meeting.has_transcript && (
                            <FileText className="h-4 w-4 text-green-500" title="Has transcript" />
                          )}
                          {meeting.has_mom && (
                            <CheckCircle className="h-4 w-4 text-blue-500" title="Has MoM" />
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Team Members */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-500" />
                  Team Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                {members.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No members yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {members.slice(0, 5).map(member => (
                      <div key={member.user_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.avatar_url} />
                            <AvatarFallback className="text-xs">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {member.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {members.length > 5 && (
                      <Button variant="ghost" size="sm" className="w-full">
                        +{members.length - 5} more
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Pending Tasks */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-green-500" />
                  Pending Tasks
                  {stats.overdue_tasks > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {stats.overdue_tasks} overdue
                    </Badge>
                  )}
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/meeting-intelligence/tasks">
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {pendingTasks.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No pending tasks
                  </p>
                ) : (
                  <div className="space-y-2">
                    {pendingTasks.map(task => {
                      const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                      return (
                        <div 
                          key={task.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isOverdue ? 'border-red-200 bg-red-50 dark:bg-red-900/10' : 'border-transparent bg-slate-50 dark:bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              task.priority === 'high' || task.priority === 'critical' 
                                ? 'bg-red-500' 
                                : task.priority === 'medium' 
                                  ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                            }`} />
                            <div>
                              <p className="text-sm font-medium">{task.title}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {task.assigned_to_name && (
                                  <span>→ {task.assigned_to_name}</span>
                                )}
                                {task.due_date && (
                                  <span className={isOverdue ? 'text-red-500' : ''}>
                                    Due: {formatDate(task.due_date)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Badge className={taskStatusColors[task.status]}>
                            {task.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Recent Decisions */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-500" />
                  Key Decisions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentDecisions.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No decisions recorded
                  </p>
                ) : (
                  <div className="space-y-3">
                    {recentDecisions.map(decision => (
                      <div key={decision.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border-l-4 border-purple-500">
                        <p className="text-sm">{decision.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(decision.created_at)}
                          </span>
                          <Badge variant="outline" className={priorityColors[decision.impact]}>
                            {decision.impact} impact
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="meetings" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground py-8">
                View all meetings in this project
                <br />
                <Button 
                  variant="link" 
                  onClick={() => navigate(`/meeting-intelligence/projects/${projectId}/meetings`)}
                >
                  Go to Project Meetings
                </Button>
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="tasks" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground py-8">
                View all tasks in this project
                <br />
                <Button 
                  variant="link" 
                  onClick={() => navigate('/meeting-intelligence/tasks')}
                >
                  Go to Task Board
                </Button>
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Project Timeline
              </CardTitle>
              <CardDescription>
                Activity history for this project
              </CardDescription>
            </CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No activity yet
                </p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                  <div className="space-y-4">
                    {timeline.map((event, idx) => {
                      const EventIcon = eventTypeIcons[event.event_type] || Activity;
                      return (
                        <div key={event.id || idx} className="relative pl-10">
                          <div className="absolute left-0 p-2 rounded-full bg-background border">
                            <EventIcon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm">{event.title}</p>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(event.timestamp)} {formatTime(event.timestamp)}
                              </span>
                            </div>
                            {event.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {event.description}
                              </p>
                            )}
                            {event.actor_name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                by {event.actor_name}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
